import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { LogoDark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrendingUp, ShieldCheck, Activity } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-slate-950 p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-30"
          style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #1E3A8A 0%, transparent 45%), radial-gradient(circle at 80% 70%, #0e7490 0%, transparent 40%)" }} />
        <div className="relative z-10">
          <img src="/logo-themensclinic.png" alt="The Men's Clinic" className="h-11 w-auto invert" />
        </div>

        <div className="relative z-10 space-y-6">
          <h2 className="font-heading text-4xl font-extrabold text-white leading-tight">
            Grow your practice.<br />
            <span className="text-cyan-400">Retain every patient.</span>
          </h2>
          <p className="text-slate-400 max-w-md">
            A precision growth engine wired into your DrChrono EMR — lead pipeline, patient
            reactivation, referral analytics and no-show reduction in one command center.
          </p>
          <div className="space-y-3 pt-2">
            {[
              { icon: TrendingUp, t: "Convert more consults into active patients" },
              { icon: Activity, t: "Reactivate lapsed patients automatically" },
              { icon: ShieldCheck, t: "Reduce no-shows with smart reminders" },
            ].map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 + i * 0.1 }}
                className="flex items-center gap-3 text-slate-300 text-sm">
                <span className="h-8 w-8 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
                  <f.icon className="h-4 w-4 text-cyan-400" />
                </span>
                {f.t}
              </motion.div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-slate-600 text-xs">HIPAA-conscious · EMR-synced · Built for men's health</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F8FAFC]">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><LogoDark /></div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 tracking-tight">Welcome back</h1>
          <p className="text-slate-500 mt-1 mb-8 text-sm">Sign in to your practice growth dashboard.</p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@clinic.com" required
                className="h-11" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link"
                  className="text-xs font-medium text-cyan-700 hover:text-cyan-900">Forgot password?</Link>
              </div>
              <Input id="password" data-testid="login-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="h-11" />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <Button data-testid="login-submit-button" type="submit" disabled={loading}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
